import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';

// Impostazioni → Estrattore → Ricerca: accesa o spenta, quante ricerche al giorno (di base 15), "Escludi le aste"
// (accesa di base, backend/aste.py), i numeri di oggi, e il punteggio da 0 a 100 degli annunci e delle case in archivio
// (backend/corrispondenza.py): i pesi, la tolleranza sul budget (di base +5%), la soglia per proporre (40) e quella per
// suggerire la casa ad altri clienti (60).
// Dell'agenzia conta la DICHIARAZIONE, non la collaborazione: «dichiarata» +30, «non dichiara» -25, «in attesa» niente.
// Il peso «Agenzia che collabora» non esiste più (la collaborazione resta solo nella scheda dell'agenzia).
const ORDINE_PESI = ['nel_budget', 'budget_90', 'dichiarata', 'non_dichiara', 'zona_preferita', 'confinante', 'requisiti'];
const IN_MENO = ['non_dichiara', 'confinante'];  // non entrano nel massimo: uno toglie punti, l'altro esclude «zona preferita»

const daDati = (d) => ({
  limite: String(d.limite), tolleranza: String(d.tolleranza), soglia: String(d.soglia), soglia_incroci: String(d.soglia_incroci),
  pesi: Object.fromEntries(ORDINE_PESI.map((k) => [k, String(d.pesi?.[k] ?? '')])),
});
const numero = (v) => Number(String(v).replace(',', '.'));

const RicercaAutomatica = () => {
  const [dati, setDati] = useState(null);
  const [modulo, setModulo] = useState(null);
  const carica = (d) => { setDati(d); setModulo(daDati(d)); };
  useEffect(() => { axios.get(`${API}/admin/ricerca-automatica`).then((r) => carica(r.data)).catch(() => {}); }, []);
  const salva = async (corpo, testo = 'Salvato') => {
    try { const r = await axios.put(`${API}/admin/ricerca-automatica`, corpo); carica(r.data); toast.success(testo); }
    catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Non salvato'); }
  };
  if (!dati || !modulo) return null;
  const cambiato = JSON.stringify(modulo) !== JSON.stringify(daDati(dati));
  const salvaTutto = () => salva({
    limite: numero(modulo.limite), tolleranza: numero(modulo.tolleranza), soglia: numero(modulo.soglia), soglia_incroci: numero(modulo.soglia_incroci),
    pesi: Object.fromEntries(ORDINE_PESI.map((k) => [k, numero(modulo.pesi[k])])),
  });
  const base = dati.predefiniti || {};
  const ripristina = () => salva({ ...base, pesi: base.pesi }, 'Tornati i valori di base');
  const input = (valore, cambia, etichetta, largo = '4.5rem') => (
    <input className="imp-input" style={{ width: largo }} inputMode="numeric" value={valore} onChange={(e) => cambia(e.target.value)} aria-label={etichetta} />
  );
  const massimo = ORDINE_PESI.filter((k) => !IN_MENO.includes(k)).reduce((t, k) => t + (numero(modulo.pesi[k]) || 0), 0);
  const peso = (k) => (dati.pesi?.[k] ?? base.pesi?.[k] ?? 0);
  return (
    <section className="card" data-testid="ricerca-automatica">
      <div className="ttl"><div className="i b"><Icona nome="cerca" /></div><h2>Ricerca automatica degli annunci</h2>
        <span className={`tag ${dati.attiva ? 't-g' : 't-r'}`}>{dati.attiva ? 'accesa' : 'spenta'}</span></div>
      <p className="imp-nota">
        Per i clienti in Ricerca o in Visita con budget e comuni (fino a 30, anche dalla zona disegnata sulla mappa), l'estensione apre ogni giorno le pagine dei risultati di immobiliare.it,
        idealista e casa.it con tutti i filtri che il portale ha (senza aprire gli annunci), in orario d'ufficio e dopo tutto il resto. Gli altri
        requisiti li controlliamo noi sulle schede lette. Gli annunci nuovi li vedete in Oggi e nella scheda del cliente: non si propone niente da solo.
      </p>
      <div className="nums n4" style={{ marginTop: '1rem', marginBottom: 0 }}>
        <div className="num"><b>{dati.fatte_oggi}</b><span>ricerche fatte oggi</span></div>
        <div className="num"><b>{dati.limite}</b><span>al giorno al massimo</span></div>
        <div className="num"><b>{dati.ricerche_attive}</b><span>ricerche accese</span></div>
        <div className="num g"><b>{dati.annunci_nuovi}</b><span>annunci da guardare</span></div>
      </div>
      <div className="imp-azioni">
        <label className="spunta"><input type="checkbox" checked={dati.attiva} onChange={(e) => salva({ attiva: e.target.checked })} aria-label="Ricerca automatica accesa" />accesa</label>
        <label className="spunta">al giorno al massimo {input(modulo.limite, (v) => setModulo({ ...modulo, limite: v }), 'Ricerche al giorno')}</label>
        <label className="spunta">
          <input type="checkbox" checked={dati.escludi_aste !== false} onChange={(e) => salva({ escludi_aste: e.target.checked })} aria-label="Escludi le aste" />
          escludi le aste
        </label>
        {/* spento di base (backend: SOLO_DICHIARATE_PREDEFINITO): acceso subito, quasi nessuna casa passerebbe */}
        <label className="spunta">
          <input type="checkbox" checked={dati.solo_dichiarate === true} onChange={(e) => salva({ solo_dichiarate: e.target.checked })} aria-label="Solo agenzie dichiarate" />
          solo agenzie dichiarate
        </label>
      </div>
      <p className="imp-nota">
        Le aste giudiziarie non si propongono e non si suggeriscono mai: immobiliare.it le toglie già dall'indirizzo della
        ricerca, per idealista e casa.it (che non hanno il filtro) le riconosciamo dalle parole dell'annuncio e dall'agenzia.
        Una casa all'asta inserita a mano resta, con l'etichetta «Asta».
      </p>
      {/* «Priorità alle agenzie dichiarate» non è un interruttore: è come funziona il punteggio, sempre. Un
          interruttore acceso che non salva niente direbbe una bugia, quindi qui si legge e basta. */}
      <p className="imp-nota" data-testid="priorita-dichiarate">
        <b>Priorità alle agenzie dichiarate.</b> Un&apos;agenzia che ha dichiarato di lavorare con noi vale {peso('dichiarata') > 0 ? '+' : ''}{peso('dichiarata')} punti,
        una che non dichiara ne toglie {Math.abs(peso('non_dichiara'))}; chi è ancora in attesa non guadagna e non perde niente. Vale sempre: è il punteggio, non un
        interruttore. Con <b>solo agenzie dichiarate</b> acceso si propongono soltanto immobili di agenzie che hanno dichiarato: le altre non entrano
        né nei nuovi annunci né nelle case in archivio, nemmeno con un punteggio alto.
      </p>

      <h3 className="imp-sotto" style={{ marginTop: '1.3rem' }}>Punteggio degli annunci e delle case in archivio</h3>
      <p className="imp-nota">
        Ogni annuncio proposto ha un punteggio da 0 a 100 con il perché e cosa manca. Fuori del tutto: oltre il budget con la tolleranza, altro
        tipo di casa, fuori dai comuni e dai confinanti, meno locali del minimo. Bagni, metri, piano, giardino, terrazzo e box non escludono: se
        mancano o non si sanno, niente punti dei requisiti.
      </p>
      <div className="imp-righe pesi" data-testid="pesi">
        {ORDINE_PESI.map((k) => (
          <div key={k}>
            <span className="nome-peso">{dati.nomi_pesi?.[k] || k}</span>
            <small className="di-base">di base {base.pesi?.[k] > 0 ? '+' : ''}{base.pesi?.[k]}</small>
            {input(modulo.pesi[k], (v) => setModulo({ ...modulo, pesi: { ...modulo.pesi, [k]: v } }), `Peso: ${dati.nomi_pesi?.[k] || k}`)}
          </div>
        ))}
        <div>
          <span className="nome-peso">Tolleranza sopra il budget massimo (%)</span><small className="di-base">di base {base.tolleranza}%</small>
          {input(modulo.tolleranza, (v) => setModulo({ ...modulo, tolleranza: v }), 'Tolleranza sul budget')}
        </div>
        <div>
          <span className="nome-peso">Sotto questo punteggio non si propone</span><small className="di-base">di base {base.soglia}</small>
          {input(modulo.soglia, (v) => setModulo({ ...modulo, soglia: v }), 'Soglia per proporre')}
        </div>
        <div>
          <span className="nome-peso">Da questo punteggio va in «Case in archivio» e nei suggeriti per gli altri clienti</span><small className="di-base">di base {base.soglia_incroci}</small>
          {input(modulo.soglia_incroci, (v) => setModulo({ ...modulo, soglia_incroci: v }), 'Soglia per gli altri clienti')}
        </div>
      </div>
      <p className="imp-nota" style={{ marginTop: '.5rem' }}>Con questi pesi il massimo è {massimo} (oltre 100 conta 100).</p>
      <div className="imp-azioni">
        <button type="button" className="btn sm p" disabled={!cambiato} onClick={salvaTutto}>Salva</button>
        <button type="button" className="btn sm" onClick={ripristina}>Valori di base</button>
      </div>
    </section>
  );
};

export default RicercaAutomatica;
